import { getRecordedCouponReach } from './couponScope';
import { getFeeLabel } from './pageFees';
import {
  DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL,
  getSubscriptionAmountExcludingContribution,
} from './processingFeeContribution';
import { getRecordedCouponDiscount } from './recordedCouponDiscount';
import { getSubscriptionCouponBase } from './subscriptionCouponBase';
import { formatTaxLineLabel, getSettledTaxBehavior, getTaxDisplayName } from './taxLineLabel';

/* eslint-disable @typescript-eslint/no-explicit-any -- records arrive as API JSON of several shapes */
type Rec = any;

/**
 * The words an order summary uses. The dashboard reads them in English; the customer portal passes
 * its translations. Every other figure and name comes from the record.
 */
export type OrderSummaryLabels = {
  subtotal: string;
  bookingFees: string;
  bookingFee: string;
  bulkDiscount: string;
  discount: string;
  complimentary: string;
  dynamicPrice: string;
  discountedFrom: string;
  taxAmount: string;
  includes: (tax: string) => string;
  total: string;
  subscriptionAmount: string;
  oneTimeItems: string;
  paid: string;
  due: string;
  refunded: string;
};

const ENGLISH_ORDER_SUMMARY_LABELS: OrderSummaryLabels = {
  subtotal: 'Subtotal',
  bookingFees: 'Booking fees',
  bookingFee: 'Booking fee',
  bulkDiscount: 'Bulk discount',
  discount: 'Discount',
  complimentary: 'Complimentary',
  dynamicPrice: 'Dynamic product price',
  discountedFrom: 'Discounted from',
  taxAmount: 'Tax amount',
  includes: tax => `Includes ${tax}`,
  total: 'Total amount',
  subscriptionAmount: 'Subscription amount',
  oneTimeItems: 'One-time items',
  paid: 'Amount paid',
  due: 'Amount due',
  refunded: 'Refunded amount',
};

export type OrderSummaryOptions = {
  /** Formats an amount in the record's currency, for names that carry one (a fixed fee). */
  money: (amount: number) => string;
  labels?: Partial<OrderSummaryLabels>;
};

export type OrderSummaryRow = {
  key: string;
  kind: string;
  label: string;
  amount: number;
  info?: boolean;
  included?: boolean;
  bold?: boolean;
  rule?: boolean;
  recurring?: boolean;
  after?: boolean;
  plan?: boolean;
};

const labelsOf = (options: OrderSummaryOptions) => ({
  ...ENGLISH_ORDER_SUMMARY_LABELS,
  ...options.labels,
});

const sum = (values: Array<number | null | undefined>) =>
  values.reduce<number>((total, value) => total + (value || 0), 0);

/** Sum of the refund transactions. */
export const getRefundedAmount = (payment: Rec) =>
  (payment.transactions ?? []).reduce(
    (total: number, transaction: Rec) =>
      transaction.status === 'refunded' || transaction.status === 'partially_refunded'
        ? total + transaction.amount
        : total,
    0,
  );

export const hasOrderItems = (record: Rec) => record.orderItems?.length > 0;

/** Every bulk discount on the charge: the product tier plus the per-variant tiers, each stored as a total. */
export const getBulkDiscountTotal = (payment: Rec) =>
  (payment.discount?.amount || 0) +
  sum(
    (payment.variants ?? []).map((variant: Rec) =>
      variant.selectedOptionId
        ? variant.selectedOptionDiscount?.amount
        : sum((variant.selectedOptions ?? []).map((option: Rec) => option.discount?.amount)),
    ),
  );

/** The tax row's name and rate; the totals add "Includes" when the tax sat inside the prices. */
export const getTaxLabel = (tax: Rec) =>
  tax.taxType === 'fixed'
    ? formatTaxLineLabel({
        name: tax.taxRateDetails?.taxType,
        percentage: tax.taxRateDetails?.percentageDecimal,
      })
    : formatTaxLineLabel({
        name: getTaxDisplayName(tax.taxType),
        percentage: tax.taxRateDetails?.percentageDecimal,
        region: [tax.taxRateDetails?.country, tax.taxRateDetails?.state].filter(Boolean).join(', '),
      });

const getTaxRows = (payment: Rec, labels: OrderSummaryLabels) =>
  payment.taxBreakdown?.length > 0
    ? payment.taxBreakdown.map((tax: Rec) => ({ label: getTaxLabel(tax), amount: tax.taxAmount }))
    : payment.taxAmount || payment.taxSource
      ? [{ label: labels.taxAmount, amount: payment.taxAmount || 0 }]
      : [];

/**
 * Pairs each `setup_fee` order line with the product or bump line it belongs to, so the fee reads as
 * a sub-line of that row. The API writes a fee line straight after its parent, and both carry the
 * same product id when the record has one; the nearest preceding line of the same kind is the
 * fallback for lines that carry none. Returns `[{ item, setupFee }]` for every other line.
 */
const sameId = (a: Rec, b: Rec) =>
  a != null && b != null && String(a?._id ?? a) === String(b?._id ?? b);

export const nestSetupFees = (items: Rec[] = []) => {
  const rows = items
    .map((item, index) => ({ item, index, setupFee: null as Rec }))
    .filter(({ item }) => item.type !== 'setup_fee');
  items.forEach((fee, index) => {
    if (fee.type !== 'setup_fee') return;
    const candidates = rows.filter(
      row => Boolean(row.item.isOrderBump) === Boolean(fee.isOrderBump) && !row.setupFee,
    );
    const parent =
      candidates.find(row => sameId(row.item.product, fee.product)) ??
      [...candidates].reverse().find(row => row.index < index) ??
      candidates[0];
    if (parent) parent.setupFee = fee;
  });
  return rows.map(({ item, setupFee }) => ({ item, setupFee }));
};

/** A bump's or add-on's setup fee line is not the primary's. */
export const hasPrimarySetupFeeLine = (items: Rec[] = []) =>
  items.some(item => item.type === 'setup_fee' && !item.isOrderBump);

/**
 * The per-ticket bulk discount. A ticket's stored `amountOff` discount is its total across the
 * quantity while a `percentOff` one is per ticket, so the first is divided out.
 */
export const calculatePerTicketBulkDiscount = ({
  quantity,
  discount,
}: {
  quantity: number;
  discount?: { amountOff?: number | null; amount?: number };
}) => {
  if (!discount) return 0;
  if (discount.amountOff != null) return Math.floor((discount.amount ?? 0) / quantity);
  return discount.amount ?? 0;
};

/** A ticket line's whole bulk discount. An `amountOff` one is stored as that total already. */
const getTicketLineBulkDiscount = ({
  quantity,
  discount,
}: {
  quantity: number;
  discount?: { amountOff?: number | null; amount?: number };
}) => {
  if (!discount) return 0;
  if (discount.amountOff != null) return discount.amount ?? 0;
  return (discount.amount ?? 0) * quantity;
};

/** What a coupon takes off a price. */
const getCouponDiscount = (price: number, coupon: Rec) => {
  let discount = 0;
  if (coupon) {
    if (coupon.amountOff) discount = coupon.amountOff;
    if (coupon.percentOff) discount = price - price * (1 - coupon.percentOff / 100);
  }
  return discount;
};

const closingRows = (payment: Rec, labels: OrderSummaryLabels): OrderSummaryRow[] => {
  const rows: OrderSummaryRow[] = [
    {
      key: 'total',
      kind: 'total',
      label: labels.total,
      amount: payment.amount,
      bold: true,
      rule: true,
    },
    { key: 'paid', kind: 'paid', label: labels.paid, amount: payment.amountPaid },
    {
      key: 'due',
      kind: 'due',
      label: labels.due,
      amount: payment.amountDue,
      bold: payment.amountDue > 0,
    },
  ];
  const refunded = getRefundedAmount(payment);
  if (refunded)
    rows.push({ key: 'refunded', kind: 'refunded', label: labels.refunded, amount: refunded });
  return rows;
};

const chargeFeeTaxAndContributionRows = (
  payment: Rec,
  { fees, taxRows, included, contribution }: Rec,
  options: OrderSummaryOptions,
  labels: OrderSummaryLabels,
) => {
  const rows: OrderSummaryRow[] = [];
  fees.forEach((fee: Rec, index: number) =>
    rows.push({
      key: `fee-${index}`,
      kind: 'fee',
      label: getFeeLabel(fee, options.money),
      amount: fee.amount,
    }),
  );
  taxRows.forEach((row: Rec, index: number) =>
    rows.push({
      key: `tax-${index}`,
      kind: 'tax',
      label: included ? labels.includes(row.label) : row.label,
      amount: row.amount,
      included,
    }),
  );
  if (contribution > 0)
    rows.push({
      key: 'processing-fee-contribution',
      kind: 'processing-fee-contribution',
      label: payment.processingFeeContribution.name || DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL,
      amount: contribution,
    });
  return rows;
};

const couponAndComplimentaryRows = (
  payment: Rec,
  { coupon, complimentary }: { coupon: number; complimentary: number },
  labels: OrderSummaryLabels,
) => {
  const rows: OrderSummaryRow[] = [];
  if (payment.coupon)
    rows.push({ key: 'coupon', kind: 'coupon', label: labels.discount, amount: -coupon });
  if (complimentary > 0)
    rows.push({
      key: 'complimentary',
      kind: 'complimentary',
      label: labels.complimentary,
      amount: -complimentary,
    });
  return rows;
};

/**
 * The rows every paid record closes with, around its own discount rows: Subtotal when the rows add
 * up to the stored total, then the fees, tax, contribution and the paid, due and refunded figures.
 */
const finishTotals = (
  payment: Rec,
  {
    subtotal,
    pretax,
    fees,
    taxRows,
    discountRows,
  }: {
    subtotal: number;
    pretax: number;
    fees: Rec[];
    taxRows: Rec[];
    discountRows: OrderSummaryRow[];
  },
  options: OrderSummaryOptions,
  labels: OrderSummaryLabels,
) => {
  const tax = sum(taxRows.map((row: Rec) => row.amount));
  const behavior = getSettledTaxBehavior(payment, pretax);
  const included = behavior === 'inclusive';
  const contribution = payment.processingFeeContribution?.amount || 0;
  const reconciles = included
    ? pretax + contribution === payment.amount
    : pretax + tax + contribution === payment.amount;

  const rows: OrderSummaryRow[] = [];
  if (reconciles)
    rows.push({ key: 'subtotal', kind: 'subtotal', label: labels.subtotal, amount: subtotal });
  rows.push(
    ...discountRows,
    ...chargeFeeTaxAndContributionRows(
      payment,
      { fees, taxRows, included, contribution },
      options,
      labels,
    ),
    ...closingRows(payment, labels),
  );
  return { rows, subtotal, reconciles, taxBehavior: behavior };
};

/**
 * A one-time payment's totals as money, from the charge's stored figures. Item amounts are stored net
 * of bulk discounts, so Subtotal adds them back and the Bulk discount row takes them off again. Rows
 * marked `info` (dynamic price, discounted from) and `included` (a tax inside the prices) are not part
 * of the sum. `reconciles` is false when the rows do not add up to the stored total, and Subtotal is
 * then left out rather than show a sum that is wrong.
 */
export const getChargeTotals = (payment: Rec, options: OrderSummaryOptions) => {
  const labels = labelsOf(options);
  const items = payment.orderItems ?? [];
  // The order lines leave the primary's setup fee out; the record holds it.
  const itemTotal = hasOrderItems(payment)
    ? sum(items.map((item: Rec) => item.amount)) +
      (hasPrimarySetupFeeLine(items) ? 0 : payment.setupFee || 0)
    : payment.productPrice || 0;
  const bulk = getBulkDiscountTotal(payment);
  const coupon = payment.coupon?.discount || 0;
  const complimentary = payment.isComplimentary ? payment.complimentaryDiscountAmount || 0 : 0;
  const fees = payment.fees ?? [];

  const rows: OrderSummaryRow[] = [];
  if (payment.dynamicPrice)
    rows.push({
      key: 'dynamic',
      kind: 'info',
      label: labels.dynamicPrice,
      amount: payment.dynamicPrice,
      info: true,
    });
  if (payment.discountedFromPrice)
    rows.push({
      key: 'discounted-from',
      kind: 'discounted-from',
      label: labels.discountedFrom,
      amount: payment.discountedFromPrice,
      info: true,
    });
  if (bulk > 0) rows.push({ key: 'bulk', kind: 'bulk', label: labels.bulkDiscount, amount: -bulk });
  rows.push(...couponAndComplimentaryRows(payment, { coupon, complimentary }, labels));

  return finishTotals(
    payment,
    {
      subtotal: itemTotal + bulk,
      pretax: itemTotal - coupon - complimentary + sum(fees.map((fee: Rec) => fee.amount)),
      fees,
      taxRows: getTaxRows(payment, labels),
      discountRows: rows,
    },
    options,
    labels,
  );
};

const perUnitBulkOff = (total: number, quantity: number) =>
  quantity > 0 ? Math.floor(total / quantity) : 0;

/**
 * One row per selected variant option: single select reads the selected option, multiple select
 * reads each option. Either way the stored discount is a total, spread over the quantity for the
 * per-unit figure (fixed 3 Oct 2026; the Variants table showed a single select's total as per unit).
 */
export const getChargeVariantRows = (payment: Rec) =>
  (payment.variants ?? []).flatMap((variant: Rec) => {
    if (variant.selectedOptionId) {
      const quantity = variant.selectedOptionQuantity || 1;
      const unitPrice =
        variant.selectedOptionPayWhatYouWantAmount || variant.originalAdditionalChargeAmount || 0;
      const bulkTotal = variant.selectedOptionDiscount?.amount || 0;
      return [
        {
          key: variant._id,
          name: `${variant.name} - ${variant.selectedOptionName}`,
          quantity,
          unitPrice,
          bulkOff: perUnitBulkOff(bulkTotal, quantity),
          payWhatYouWant: variant.selectedOptionPayWhatYouWant === true,
          amount: unitPrice * quantity - bulkTotal,
        },
      ];
    }
    return (variant.selectedOptions ?? []).map((option: Rec, index: number) => {
      const quantity = option.quantity || 0;
      const unitPrice = option.payWhatYouWantAmount || option.originalAdditionalChargeAmount || 0;
      const bulkTotal = quantity > 0 ? option.discount?.amount || 0 : 0;
      return {
        key: `${variant._id}-${index}`,
        name: `${variant.name} - ${option.optionName}`,
        quantity,
        unitPrice,
        bulkOff: perUnitBulkOff(bulkTotal, quantity),
        payWhatYouWant: option.payWhatYouWant === true,
        amount: unitPrice * quantity - bulkTotal,
      };
    });
  });

/** Tickets in their groups, in order of first appearance. */
export const groupTickets = (tickets: Rec[] = []) => {
  const groups = new Map();
  tickets.forEach(ticket => {
    if (!groups.has(ticket.ticketGroup))
      groups.set(ticket.ticketGroup, {
        _id: ticket.ticketGroupId,
        name: ticket.ticketGroupName,
        tickets: [],
      });
    groups.get(ticket.ticketGroup).tickets.push(ticket);
  });
  return [...groups.values()];
};

/**
 * One ticket line's figures: per-ticket price, fee, bulk and coupon, and the line amount (fee
 * included). The fee's display follows the ticket group's "Ticket fee display" setting frozen on the
 * ticket; a ticket without one is included.
 */
export const getTicketRow = (ticket: Rec, labels: Partial<OrderSummaryLabels> = {}) => ({
  name: ticket.name,
  quantity: ticket.quantity,
  originalPrice: ticket.originalPrice,
  fee: ticket.fee?.amount > 0 ? ticket.fee.amount : 0,
  feeTotal: ticket.fee?.amount > 0 ? (ticket.fee.totalAmount ?? 0) : 0,
  feeName: ticket.fee?.name || labels.bookingFee || ENGLISH_ORDER_SUMMARY_LABELS.bookingFee,
  feeDisplay: ticket.fee?.display === 'separate' ? 'separate' : 'included',
  bulkOff:
    ticket.discount?.amount > 0
      ? calculatePerTicketBulkDiscount({ quantity: ticket.quantity, discount: ticket.discount })
      : 0,
  couponOff: ticket.coupon?.amount > 0 ? ticket.coupon.amount : 0,
  amount: ticket.price * ticket.quantity + (ticket.fee?.totalAmount ?? 0),
});

export const getAddOns = (payment: Rec) =>
  (payment.orderItems ?? []).filter((item: Rec) => item.isOrderBump);

/**
 * A booking's totals as money. Subtotal is the tickets at their price plus the booking fee lines and
 * the add-ons, before any discount. Booking fees shows the fees actually charged; it is inside the
 * Subtotal already, so it is not summed again. Bulk discounts are the whole-booking tier plus the
 * per-ticket tiers; the coupon figure is the coupon's stored discount, which covers fees too.
 */
export const getBookingTotals = (payment: Rec, options: OrderSummaryOptions) => {
  const labels = labelsOf(options);
  const tickets = payment.tickets ?? [];
  // A ticket's stored price is net of its own bulk tier; the original price is the gross.
  const ticketsGross = sum(
    tickets.map((ticket: Rec) => (ticket.originalPrice ?? ticket.price) * ticket.quantity),
  );
  const feeLinesGross = sum(tickets.map((ticket: Rec) => ticket.fee?.totalAmount));
  const hasTicketFees = tickets.some((ticket: Rec) => ticket.fee?.amount > 0);
  // The API stores the fees actually charged; bookings priced before it did fall back to the lines.
  const feesCharged = payment.ticketFeesAmount ?? feeLinesGross;
  const addOns = sum(getAddOns(payment).map((item: Rec) => item.amount));
  const orderBulk = payment.discount?.amount || 0;
  const ticketBulk = sum(
    tickets
      .filter((ticket: Rec) => ticket.discount?.amount > 0)
      .map((ticket: Rec) =>
        getTicketLineBulkDiscount({ quantity: ticket.quantity, discount: ticket.discount }),
      ),
  );
  const bulk = orderBulk + ticketBulk;
  const ticketCoupons = sum(tickets.map((ticket: Rec) => ticket.coupon?.totalAmount));
  // A record with rules can hold ticket shares and add-on shares (in `discount`) together; one
  // without them has one or the other.
  const coupon = !payment.coupon
    ? 0
    : getRecordedCouponReach(payment.coupon, { pageId: '' }).hasRules
      ? (getRecordedCouponDiscount({ ...payment.coupon, totalTicketDiscount: ticketCoupons }) ?? 0)
      : payment.coupon.discount || ticketCoupons;
  const complimentary = payment.isComplimentary ? payment.complimentaryDiscountAmount || 0 : 0;
  const fees = payment.fees ?? [];
  const subtotal = ticketsGross + feeLinesGross + addOns;

  const rows: OrderSummaryRow[] = [];
  if (hasTicketFees)
    rows.push({
      key: 'booking-fees',
      kind: 'booking-fees',
      label: labels.bookingFees,
      amount: feesCharged,
      info: true,
    });
  if (orderBulk > 0)
    rows.push({ key: 'bulk', kind: 'bulk', label: labels.bulkDiscount, amount: -orderBulk });
  if (ticketBulk > 0)
    rows.push({
      key: 'bulk-tickets',
      kind: 'bulk-tickets',
      label: labels.bulkDiscount,
      amount: -ticketBulk,
    });
  rows.push(...couponAndComplimentaryRows(payment, { coupon, complimentary }, labels));

  const totals = finishTotals(
    payment,
    {
      subtotal,
      pretax: subtotal - bulk - coupon - complimentary + sum(fees.map((fee: Rec) => fee.amount)),
      fees,
      taxRows: getTaxRows(payment, labels),
      discountRows: rows,
    },
    options,
    labels,
  );
  return { ...totals, feesCharged };
};

/**
 * One row per selected variant option: a single select is one unit at the option's charge, multiple
 * and quantity selects one row per option; recurring options carry the interval, the others are
 * one-time.
 */
export const getSubscriptionVariantRows = (subscription: Rec) =>
  (subscription.variants ?? []).flatMap((variant: Rec) => {
    if (variant.selectionType === 'single') {
      const unitPrice =
        variant.selectedOptionPayWhatYouWantAmount || variant.additionalChargeAmount || 0;
      return [
        {
          key: variant._id,
          name: `${variant.name} - ${variant.selectedOptionName}`,
          quantity: 1,
          unitPrice,
          amount: unitPrice,
          recurring: variant.selectedOptionType === 'recurring',
          payWhatYouWant: variant.selectedOptionPayWhatYouWant === true,
        },
      ];
    }
    if (variant.selectionType === 'multiple' || variant.selectionType === 'quantity') {
      return (variant.selectedOptions ?? []).map((option: Rec, index: number) => {
        const quantity = option.quantity || 0;
        const unitPrice = option.payWhatYouWantAmount || option.additionalChargeAmount || 0;
        return {
          key: `${variant._id}-${index}`,
          name: `${variant.name} - ${option.optionName}`,
          quantity,
          unitPrice,
          amount: unitPrice * quantity,
          recurring: option.optionType === 'recurring',
          payWhatYouWant: option.payWhatYouWant === true,
        };
      });
    }
    return [];
  });

/** What the coupon takes off each charge: the recurring product and its options, not fees or bumps. */
export const getSubscriptionCouponMoney = (subscription: Rec) =>
  getCouponDiscount(getSubscriptionCouponBase(subscription), subscription.coupon);

/**
 * What the customer adds to each charge to cover the processing fee, worked out on the charge's
 * total with tax. A coupon that runs past the first charge keeps lowering it.
 */
export const getRenewalProcessingFeeContribution = (subscription: Rec) => {
  const contribution = subscription.processingFeeContribution;
  if (!contribution) return 0;
  const discounted = subscription.coupon && subscription.coupon.duration !== 'once';
  return (
    (discounted ? contribution.discountedRenewalAmount : undefined) ??
    contribution.renewalAmount ??
    0
  );
};

/**
 * A subscription's totals as money. The stored `amount` is the recurring price including recurring
 * variant options, fees and the processing fee contribution, before any coupon; so Subtotal is the
 * recurring items without fees, the fee rows add up to "Subscription amount", and the coupon's
 * discount sits beneath it as what comes off each charge. One-time options and bumps are listed as
 * information. `period` and `schedule` add the billing lines (renewal date, plan end) when given.
 */
export const getSubscriptionTotals = (
  subscription: Rec,
  options: OrderSummaryOptions & {
    period?: (subscription: Rec) => string | undefined;
    schedule?: (subscription: Rec) => string | undefined;
  },
) => {
  const labels = labelsOf(options);
  const items = subscription.orderItems ?? [];
  const variants = getSubscriptionVariantRows(subscription);
  const oneTimeVariants = sum(
    variants.filter((row: Rec) => !row.recurring).map((row: Rec) => row.amount),
  );
  const recurringItems = items.filter(
    (item: Rec) => item.billingType === 'recurring' && item.type !== 'setup_fee',
  );
  const oneTimeItems = items.filter(
    (item: Rec) => item.billingType !== 'recurring' && item.type !== 'setup_fee',
  );
  // A recurring item's amount holds the options it was bought with, one-time ones included.
  const recurring = hasOrderItems(subscription)
    ? sum(recurringItems.map((item: Rec) => item.amount)) - oneTimeVariants
    : (subscription.productPrice || 0) +
      sum(variants.filter((row: Rec) => row.recurring).map((row: Rec) => row.amount));
  const fees = subscription.fees ?? [];
  const feeTotal = sum(fees.map((fee: Rec) => fee.amount));
  const subscriptionAmount = getSubscriptionAmountExcludingContribution(subscription);
  const reconciles = recurring + feeTotal === subscriptionAmount;
  const oneTime = sum(oneTimeItems.map((item: Rec) => item.amount)) + oneTimeVariants;
  const coupon = subscription.coupon ? getSubscriptionCouponMoney(subscription) : 0;

  const rows: OrderSummaryRow[] = [];
  if (reconciles)
    rows.push({
      key: 'subtotal',
      kind: 'subtotal',
      label: labels.subtotal,
      amount: recurring,
      recurring: true,
    });
  if (oneTime > 0)
    rows.push({
      key: 'one-time',
      kind: 'one-time',
      label: labels.oneTimeItems,
      amount: oneTime,
      info: true,
    });
  if (subscription.dynamicPrice)
    rows.push({
      key: 'dynamic',
      kind: 'info',
      label: labels.dynamicPrice,
      amount: subscription.dynamicPrice,
      info: true,
      recurring: true,
    });
  if (subscription.discountedFromPrice)
    rows.push({
      key: 'discounted-from',
      kind: 'discounted-from',
      label: labels.discountedFrom,
      amount: subscription.discountedFromPrice,
      info: true,
      recurring: true,
    });
  fees.forEach((fee: Rec, index: number) =>
    rows.push({
      key: `fee-${index}`,
      kind: 'fee',
      label: getFeeLabel(fee, options.money),
      amount: fee.amount,
      recurring: true,
    }),
  );
  rows.push({
    key: 'total',
    kind: 'total',
    label: labels.subscriptionAmount,
    amount: subscriptionAmount,
    bold: true,
    rule: true,
    recurring: true,
  });
  if (subscription.coupon)
    rows.push({
      key: 'coupon',
      kind: 'coupon',
      label: labels.discount,
      amount: -coupon,
      after: true,
    });
  const contribution = getRenewalProcessingFeeContribution(subscription);
  if (contribution)
    rows.push({
      key: 'processing-fee-contribution',
      kind: 'processing-fee-contribution',
      label:
        subscription.processingFeeContribution.name || DEFAULT_PROCESSING_FEE_CONTRIBUTION_LABEL,
      amount: contribution,
      after: true,
      recurring: true,
    });
  const period = options.period?.(subscription);
  if (period)
    rows.push({
      key: 'billing',
      kind: 'billing',
      label: period,
      plan: false,
      amount: 0,
      after: true,
      info: true,
    });
  const schedule = options.schedule?.(subscription);
  if (schedule)
    rows.push({
      key: 'billing-plan',
      kind: 'billing',
      label: schedule,
      plan: true,
      amount: 0,
      after: true,
      info: true,
    });

  return { rows, recurring, reconciles, coupon, oneTime };
};

/**
 * A subscription payment's totals: it stores only its amount, so there is no tax or fee breakdown.
 * A refund recorded without its transactions refunds the whole payment.
 */
export const getSubscriptionPaymentTotals = (payment: Rec, options: OrderSummaryOptions) => {
  const labels = labelsOf(options);
  const refunded = getRefundedAmount(payment) || (payment.refunded ? payment.amount : 0);
  const rows: OrderSummaryRow[] = [
    {
      key: 'total',
      kind: 'total',
      label: labels.total,
      amount: payment.amount,
      bold: true,
      rule: true,
    },
  ];
  if (refunded)
    rows.push({ key: 'refunded', kind: 'refunded', label: labels.refunded, amount: refunded });
  return { rows };
};
